import { FC, ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { PlanType } from "sdk/types";
import { routes, RoutesValues } from "~/constants";
import { useCurrentUser } from "~/hooks/useCurrentUser";
import { useQuery } from "~/hooks/useQuery";

interface Props {
  protectAuth?: boolean;
  protectConfirmation?: boolean;
  protectOnboarding?: boolean;
  protectPayment?: boolean;
  protectPaymentIssue?: boolean;
  protectSetPassword?: boolean;
  requireAuth?: boolean;
  requireGoodStanding?: boolean;
  requireNoPause?: boolean;
  requireOnboarding?: boolean;
  requirePayment?: boolean;
  requireSetPassword?: boolean;
  children: ReactNode;
}

const navigateTo = (route: RoutesValues, state: unknown): JSX.Element => {
  return <Navigate replace={true} state={state} to={route} />;
};

type User = ReturnType<typeof useCurrentUser>;

interface NextRoute {
  route: RoutesValues | null;
  state?: any;
}

export const getNextRoute = (
  user: User,
  {
    protectAuth,
    protectConfirmation,
    protectOnboarding,
    protectPayment,
    protectPaymentIssue,
    protectSetPassword,
    requireAuth,
    requireGoodStanding,
    requireNoPause,
    requireOnboarding,
    requirePayment,
    requireSetPassword,
  }: Omit<Props, "children">,
): NextRoute => {
  const { externalId, isOnboarded, isPasswordSet } = user ?? {};
  const status = user?.subscription?.status ?? null;
  const canceledAt = user?.subscription?.canceledAt ?? null;
  const partnerSlug = user?.partner?.slug ?? null;
  const partnerActivatedAt = user?.partner?.activatedAt ?? null;
  const partnerEndedAt = user?.partner?.endedAt ?? null;
  const isPartnerTrialEnded =
    !!partnerEndedAt && new Date(partnerEndedAt) < new Date();
  const isSubscriptionTrialing = status === "trialing";

  const hasActiveSubscription = !!status && status !== "incomplete";
  const hasActivatedPartnerTrial = !!partnerSlug && !!partnerActivatedAt;
  const isTrialOfferActive = hasActivatedPartnerTrial && !isPartnerTrialEnded;

  const hasPaid = hasActiveSubscription;
  const canExtend = isTrialOfferActive && isSubscriptionTrialing;
  const needsPartnerActivation =
    !hasActiveSubscription && partnerSlug && !partnerActivatedAt;
  const isFullyCanceled = status === "canceled" && !!canceledAt;
  const needsPartnerActivationPayload =
    partnerSlug && !partnerActivatedAt
      ? {
          route: routes.partnerActivate.replace(
            ":slug",
            partnerSlug,
          ) as RoutesValues,
        }
      : { route: routes.dashboard };
  const isOperational = status === "active" || status === "trialing";
  const isAuthenticated = !!externalId;
  const hasPaymentIssue = isAuthenticated && !isOperational && !isFullyCanceled;
  const isPaused = status == "paused";

  // This order is important. Users should be redirected in a way that mirrors the onboarding flow.
  if (requireAuth && !externalId) return { route: routes.signIn };
  if (requireOnboarding && !isOnboarded)
    return { route: routes.onboardingRoot };
  if (requirePayment && isFullyCanceled)
    return { route: routes.planSelect, state: { resubscribe: true } };
  if (requirePayment && needsPartnerActivation)
    return needsPartnerActivationPayload;
  if (requirePayment && !hasPaid) return { route: routes.planPayment };
  if (requireSetPassword && !isPasswordSet)
    return { route: routes.setPassword };
  if (requireNoPause && isPaused) return { route: routes.settings };
  if (requireGoodStanding && isPaused) return { route: routes.settings };
  if (requireGoodStanding && hasPaymentIssue)
    return { route: routes.paymentIssue };

  const defaultPage = routes.dashboard;

  if (protectAuth && externalId) return { route: defaultPage };
  if (protectConfirmation && hasPaid && !isFullyCanceled && !canExtend)
    return { route: defaultPage };
  if (protectOnboarding && isOnboarded) return { route: defaultPage };
  if (protectPayment && hasPaid && !isFullyCanceled && !canExtend)
    return { route: defaultPage };

  if (protectSetPassword && isPasswordSet) return { route: defaultPage };
  if (protectPaymentIssue && !hasPaymentIssue) return { route: defaultPage };

  return { route: null };
};

const types: PlanType[] = ["plus", "premium"];

export const Protector: FC<Props> = ({ children, ...props }) => {
  const user = useCurrentUser();
  const location = useLocation();
  const query = useQuery();
  const type = query.get("type");

  const typeState = types.includes(type as any) ? { type } : {};
  const locationState = { ...typeState, ...location.state };

  const { route, state } = getNextRoute(user, props);
  const nextState = { ...locationState, ...state };

  return route ? navigateTo(route, nextState) : <>{children}</>;
};
